import * as _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { isFreeTier } from '../../../../../shared/subscriptions';
import { useDialogContext } from '../../../../contexts/DialogContext';
import { useAddToast } from '../../../../contexts/ToastContext';
import { window } from '../../../../globals';
import {
  errorMessageFromResponse,
  errorMessagesListFromResponse,
  type FailedFetchResponse,
} from '../../../../helpers/errors';
import { useAsyncValue } from '../../../../hooks';
import { get, patch, post, remove } from '../../../../redux/api';
import { selectOptionFactory } from '../../../../utils';
import type {
  JiraComponent,
  JiraCredentials,
  JiraCustomField,
  JiraHeaders,
  JiraResponse,
  JiraUpdateRequest,
} from '../../../../../shared/jira';
import type { Jira } from '../../../../../shared/model-typings';
import type { DeleteJiraResponse, RegenerateJiraWebhookResponse } from '../../../../../typings/api';

import type { JiraSettingsComponentsProps } from './JiraSettingsComponents';
import type { JiraSettingsConfigurationProps } from './JiraSettingsConfiguration';
import type { JiraSettingsCredentialsProps } from './JiraSettingsCredentials';
import type { JiraSettingsCustomFieldsProps } from './JiraSettingsCustomFields';
import type { JiraHeaderItem, JiraSettingsHeadersTableProps } from './JiraSettingsHeadersTable';
import type { JiraSettingsSiteSelectionProps } from './JiraSettingsSiteSelection';
import type { JiraSettingsStatusProps } from './JiraSettingsStatus';

type OnAddSiteFn = (site: string) => Promise<void>;

//  Return is broken into objects representing each UI component's props.
type UseJiraSettingsReturn = {
  hasData: true;
  // onClickAddSite is passed in from a child component
  siteSelection: Omit<JiraSettingsSiteSelectionProps, 'onClickAddSite'>;
  configuration: JiraSettingsConfigurationProps;
  credentials: JiraSettingsCredentialsProps;
  status: JiraSettingsStatusProps;
  customFields: JiraSettingsCustomFieldsProps;
  headers: JiraSettingsHeadersTableProps;
  components: JiraSettingsComponentsProps;
  errors: string[] | null;
  onAddSite: OnAddSiteFn;
  onSaveSite: () => Promise<void>;
  isSaveButtonEnabled: boolean,
  isLoading: boolean;
};

type NoJiraSettingsReturn = {
  hasData: false;
  onAddSite: OnAddSiteFn;
  isLoading: boolean;
};

/**
 * Factory for creating a new (minimal) Jira object.
 */
function createNewJira(name: string) {
  return post<{ name: string, enabled: boolean }, JiraResponse>(`/api/jira`, { name, enabled: true });
}

/**
 * Request to delete this Jira instance.
 */
async function deleteJira(jiraId: number) {
  return remove<never, DeleteJiraResponse>(`/api/jira/${jiraId}`);
}

/**
 * Request to save this Jira instance.
 */
async function saveJira(jira: JiraUpdateRequest) {
  return patch<JiraUpdateRequest, void>(`/api/jira/${jira.id}`, jira);
}

/**
 * Request to regenerate the webhookURL for this Jira site.
 */
async function regenerateWebhook(jiraId: number) {
  return post<unknown, RegenerateJiraWebhookResponse>(`/api/jira/${jiraId}/regenerate_auth`, {});
}

/**
 * Helper to check if a header row is empty.
 */
const isHeaderEmpty = (header: JiraHeaderItem): boolean =>
  header.headerKey.trim() === '' ||
  header.value.trim() === '';

/**
 * Jira headers are objects, however dealing with mutating objects in component state can become challenging.
 * Because of this challenge, we transform the objects into arrays so dealing with state change is simple.
 * We eventually transform the arrays back into objects before hitting the API
 */
function headersToList(headers: JiraHeaders): JiraHeaderItem[] {
  return Object.entries(headers).map(([key, value]: [string, unknown]) => ({
    headerKey: key,
    value,
  }));
}

/**
 * Jira custom fields are objects, however dealing with mutating objects in component state can become challenging.
 * Because of this challenge, we transform the objects into arrays so dealing with state change is simple.
 * We eventually transform the arrays back into objects before hitting the API
 */
function customFieldsToList(fields: Record<string, JiraCustomField>): JiraCustomField[] {
  return Object.values(fields);
}

/**
 * Jira custom fields are objects, however dealing with mutating objects in component state can become challenging.
 * Because of this challenge, we transform the objects into arrays so dealing with state change is simple.
 * This function transforms the arrays back into objects before hitting the API
 */
function transformCustomFields(
  nextFields: JiraCustomField[],
): Record<string, JiraCustomField> | null {
  const fields = Object.fromEntries(
    nextFields
      .filter(field => !!field.fieldId)
      .map((field) => [field.fieldId, field]));

  return _.isEmpty(fields) ? null : fields;
}

/**
 * Jira headers are objects, however dealing with mutating objects in component state can become challenging.
 * Because of this challenge, we transform the objects into arrays so dealing with state change is simple.
 * This function transforms the arrays back into objects before hitting the API
 */
function transformHeaders(
  nextHeaders: JiraHeaderItem[],
): JiraHeaders | null {
  const fields = Object.fromEntries(
    nextHeaders
      .filter(header => !isHeaderEmpty(header))
      .map(({ headerKey, value }) => [headerKey, value]));

  return _.isEmpty(fields) ? null : fields;
}

/**
 * sanitizeCredentials converts empty looking objects into null's to be sent to the API. We do this to ensure
 * we don't store a bunch of random objects in the DB
 */
function sanitizeCredentials(
  credentials: JiraCredentials,
): JiraCredentials | null {
  if (_.isEmpty(credentials) || _.isEmpty(credentials.basic)) {
    return null;
  }

  return credentials;
}

/**
 * Hook which powers the JiraSettings page.
 * Although the state technically lives up in the OrgSettings,
 * this hook handles sanitizing inputs and managing which Jira is being edited.
 * In addition, it also handles the request to delete a Jira instance and
 * the request to regenerate the webhook URL.
 */
const useJiraSettings = (): UseJiraSettingsReturn | NoJiraSettingsReturn => {
  const addToast = useAddToast();
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  //  Manage the state of the settings. This is used both to store the data when we fetch it,
  //  but is also used to store the changes/updates to the settings.
  const {
    value: jiraVals,
    isLoading,
    errors,
    setPending,
    setResolve: setJiras,
    setReject,
  } = useAsyncValue<JiraResponse[]>(null);

  const fetchSettings = useCallback(async () => {
    try {
      setIsDirty(false);
      setPending();
      const { data } = await get<JiraResponse[]>('/api/jira');
      setJiras(data || null);
      if (data?.[0]) {
        setActiveJiraId(data[0].id);
      }
    } catch (error: unknown) {
      setReject(errorMessagesListFromResponse(error as FailedFetchResponse).map(e => e.toString()));
    }
  }, [setJiras, setPending, setReject]);

  //  Handle fetching the orgs jiras on mount
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // memoize this value so that we can ensure `jiras` is always of type JiraResponse[], and only changes when incoming jiraVals changes
  const jiras = useMemo(() => {
    return jiraVals || [];
  }, [jiraVals]);

  //  Used when deleting a Jira.
  const { onConfirm } = useDialogContext();

  // Users can have multiple Jira sites configured. We use this to control which one is currently being viewed/edited
  const [activeJiraId, setActiveJiraId] = useState<number>(jiras[0]?.id || -1);
  const activeJira = jiras.find(j => j.id === activeJiraId);

  // Both headers & custom fields are Record's, however in the actual components, it's much easier to deal in arrays.
  // Because of this, we pass in array's each Record value, and convert them back to objects on save
  const [headers, setHeaders] = useState(headersToList(activeJira?.headers || {}));
  const [customFields, setCustomFields] = useState(customFieldsToList(activeJira?.customFields || {}));

  useEffect(() => {
    setHeaders(headersToList(activeJira?.headers || {}));
  }, [activeJira?.headers]);

  useEffect(() => {
    setCustomFields(customFieldsToList(activeJira?.customFields || {}));
  }, [activeJira?.customFields]);

  // Update the active jira's data (before saving)
  const onUpdateActiveJira = useCallback((updatedJira: Omit<Partial<Jira>, 'customFields' | 'headers'>) => {
    setIsDirty(true);
    setJiras(jiras.map(jira => jira.id !== activeJiraId ? jira : ({
      ...jira,
      ...updatedJira,
    })));
  }, [activeJiraId, jiras, setJiras]);

  // Update the credentials (before save)
  const onUpdateActiveJiraCredentials = useCallback((updatedCredentials: Partial<JiraCredentials['basic']>) =>
    onUpdateActiveJira({
      credentials: {
        basic: {
          ...((activeJira?.credentials as JiraCredentials)?.basic || {}),
          ...updatedCredentials,
        },
      },
    }), [activeJira, onUpdateActiveJira]);

  // Update the customFields (before save)
  const onUpdateCustomFields = (nextCustomFields: JiraCustomField[]) => {
    setCustomFields(nextCustomFields);
    setIsDirty(true);
  };

  // Update the headers (before save)
  const onUpdateHeaders = (nextHeaders: JiraHeaderItem[]) => {
    setHeaders(nextHeaders);
    setIsDirty(true);
  };

  const onChangeSite = useCallback(async (nextJiraSiteId: number) => {
    if (isDirty) {
      onConfirm({
        title: 'Discard pending changes to this site?',
        body: `Are you sure you want to change sites? This will discard any unsaved changes to this site.`,
        confirmText: 'Change site',
        onConfirm: async () => {
          // refetch all settings before changing sites
          await fetchSettings();
          setActiveJiraId(nextJiraSiteId);
        },
      });
    } else {
      setActiveJiraId(nextJiraSiteId);
    }
  }, [isDirty, onConfirm, fetchSettings]);

  /**
   * OnAddSite takes in the name of a Jira site, and calls the BE to create a brand new Jira row in the DB
   */
  const addSite = useCallback(async (name: string) => {
    try {
      setIsSaving(true);
      const { data: jira } = await createNewJira(name);
      if (!jira) {
        throw new Error('No data returned from API.');
      }
      addToast({ type: 'success', content: `Added new site: "${jira.name}"` });
      setJiras([
        ...jiras,
        jira,
      ]);
      setActiveJiraId(jira.id);
    } catch (error) {
      const errorMessage = errorMessageFromResponse(error as FailedFetchResponse);
      addToast({
        type: 'error',
        content: `Failed to add new Jira site. Error: ${errorMessage}`,
      });
    } finally {
      setIsSaving(false);
      setIsDirty(false);
    }
  }, [jiras, setJiras, addToast]);

  const onAddSite = useCallback(async (name: string) => {
    if (isDirty) {
      onConfirm({
        title: 'Discard pending changes?',
        body: `Changes in progress. Adding a new site will discard any unsaved changes to the current site.`,
        confirmText: 'Confirm',
        onConfirm: async () => {
          // Add new site
          await addSite(name);
          // refetch all settings before changing sites
          await fetchSettings();
        },
      });
    } else {
      await addSite(name);
    }
  }, [addSite, isDirty, onConfirm, fetchSettings]);

  /**
   * onSaveSate submits all of the user changes to an existing Jira site, and calls the BE API to save those changes in the DB.
   * Both `headers` and `customFields` are transformed from arrays into objects before calling the API
   */
  const onSaveSite = useCallback(async () => {
    if (!activeJira) {
      return;
    }

    try {
      setIsSaving(true);
      const nextJira = {
        ...activeJira,
        headers: transformHeaders(headers),
        customFields: transformCustomFields(customFields),
        credentials: sanitizeCredentials(activeJira.credentials as JiraCredentials),
      };

      await saveJira(nextJira);
      addToast({ type: 'success', content: `Saved jira ${activeJira.name}` });
    } catch (error) {
      const errorMessage = errorMessageFromResponse(error as FailedFetchResponse);
      addToast({
        type: 'error',
        content: `Failed to save jira. Error: ${errorMessage}`,
      });
    } finally {
      setIsSaving(false);
      setIsDirty(false);
    }
  }, [activeJira, addToast, headers, customFields]);

  /**
   * onDeleteSite calls the BE API to delete the `activeJira` from the DB. It then removes that Jira site from the FE as well.
   */
  const onDeleteSite = useCallback(() =>
    onConfirm({
      title: 'Delete Jira Instance?',
      body: `Are you sure you want to remove ${activeJira?.name ? `"${activeJira?.name}"` : 'this'} Jira instance from FOSSA?`,
      confirmText: 'Delete Jira',
      onConfirm: async () => {
        let wasDeleted = false;
        if (activeJiraId > 0) {
          setIsSaving(true);
          try {
            await deleteJira(activeJiraId);
            wasDeleted = true;
            addToast({ type: 'success', content: `Deleted jira ${activeJira?.name}` });
          } catch (error) {
            const errorMessage = errorMessageFromResponse(error as FailedFetchResponse);
            addToast({
              type: 'error',
              content: `Failed to delete jira ${activeJira?.name}. Error: ${errorMessage}`,
            });
          } finally {
            setIsSaving(false);
            setIsDirty(false);
          }
        }

        // Only update UI state if jira was successfully deleted
        if (wasDeleted) {
          const nextJiras = jiras.filter(jira => jira.id !== activeJiraId);
          //  If this is the last Jira, remove the one being deleted
          //  then just create a new empty Jira and set it as the active one.
          if (nextJiras.length === 0) {
            setJiras(nextJiras);
            return;
          }

          //  Remove this Jira from the list
          const nextActiveJiraIndex = nextJiras.findIndex(jira => jira.id === activeJiraId) - 1;
          const nextActiveJiraId = (jiras?.[nextActiveJiraIndex]?.id ?? nextJiras[0]?.id) as number;
          setJiras(nextJiras);
          setActiveJiraId(nextActiveJiraId);
        }
      },
    }), [jiras, activeJira, activeJiraId, setJiras, onConfirm, addToast]);

  const onRegenerateWebhook = useCallback(() =>
    onConfirm({
      title: 'Regenerate Jira Webhook URL',
      body: `This will invalidate your current Jira Webhook URL, and all future requests to this URL will be ignored.
      A new URL will be generated, and you will have to update your Jira settings to send webhook notifications to this URL.`,
      onConfirm: async () => {
        setIsSaving(true);
        try {
          const { data } = await regenerateWebhook(activeJiraId);
          if (!data?.webhookURL) {
            return;
          }
          onUpdateActiveJira({ webhookURL: data.webhookURL });
          addToast({ type: 'success', content: 'Regenerated webhook url' });
        } catch (error) {
          const errorMessage = errorMessageFromResponse(error as FailedFetchResponse);
          addToast({
            type: 'error',
            content: `Failed to regenerate webhook url. Error: ${errorMessage}`,
          });
        } finally {
          setIsSaving(false);
        }
      },
    }), [activeJiraId, onUpdateActiveJira, onConfirm, addToast]);

  if (!activeJira) {
    return {
      hasData: false,
      onAddSite,
      isLoading: isLoading || isSaving,
    };
  }

  return {
    hasData: true,
    onAddSite,
    onSaveSite,
    isLoading: isLoading || isSaving,
    isSaveButtonEnabled: isDirty && !isFreeTier({
      plan: window.user?.organization?.stripe_plan,
      stripePriceLimit: window.user?.organization?.stripePriceLimit,
    }),
    siteSelection: {
      sites: jiras.map(({ id, name }) => selectOptionFactory<number>(id, name)),
      selectedOption: activeJira.id,
      isSiteEnabled: activeJira.enabled,
      isDeletingSite: isLoading,
      onSiteSelect: onChangeSite,
      onDeleteSite,
      onToggleEnabled: (isEnabled: boolean) => onUpdateActiveJira({ enabled: isEnabled }),
    },
    configuration: {
      name: activeJira.name,
      base_url: activeJira.base_url,
      jiraProjectIds: activeJira.jiraProjectIds,
      defaultLicensingProject: activeJira.defaultLicensingProject,
      defaultSecurityProject: activeJira.defaultSecurityProject,
      defaultQualityProject: activeJira.defaultQualityProject,
      defaultUniqueTickets: activeJira.defaultUniqueTickets,
      issueTypes: activeJira.issueTypes,
      labels: activeJira.labels,
      onNameChange: (name: string) => onUpdateActiveJira({ name }),
      onBaseURLChange: (baseUrl: string) => onUpdateActiveJira({ base_url: baseUrl }),
      onProjectIdsChange: (projectIds: string[]) => onUpdateActiveJira({ jiraProjectIds: projectIds }),
      onDefaultLicensingProjectChange: (proj: string | null) => onUpdateActiveJira({ defaultLicensingProject: proj }),
      onDefaultSecurityProjectChange: (proj: string | null) => onUpdateActiveJira({ defaultSecurityProject: proj }),
      onDefaultQualityProjectChange: (proj: string | null) => onUpdateActiveJira({ defaultQualityProject: proj }),
      onDefaultUniqueTicketsChange: (uniqueTickets: boolean) =>
        onUpdateActiveJira({ defaultUniqueTickets: uniqueTickets }),
      onIssueTypesChange: (issueTypes: string[]) => onUpdateActiveJira({ issueTypes }),
      onLabelsChange: (labels: string[]) => onUpdateActiveJira({ labels }),
    },
    credentials: {
      username: (activeJira.credentials as JiraCredentials)?.basic?.username,
      password: (activeJira.credentials as JiraCredentials)?.basic?.password,
      onUsernameChange: (username: string) => onUpdateActiveJiraCredentials({ username }),
      onPasswordChange: (password: string) => onUpdateActiveJiraCredentials({ password }),
    },
    status: {
      webhookURL: activeJira.webhookURL,
      resolved_statuses: activeJira.resolved_statuses,
      resolvedStatusesEnabled: activeJira.resolvedStatusesEnabled,
      onRegenerateWebhook,
      onStatusesChange: (resolved_statuses: string[]) => onUpdateActiveJira({ resolved_statuses }), // eslint-disable-line @typescript-eslint/naming-convention
      onStatusesEnabledChange: (enabled: boolean) => onUpdateActiveJira({ resolvedStatusesEnabled: enabled }),
    },
    customFields: {
      customFields,
      onUpdateCustomFields,
    },
    headers: {
      headers,
      onUpdateHeaders,
    },
    components: {
      components: activeJira.components ?? [],
      onUpdateComponents: (components: JiraComponent[]) => onUpdateActiveJira({ components }),
    },
    errors,
  };
};

export default useJiraSettings;
